import type { DefaultValues, UseFormReturn } from 'react-hook-form';
import { Checkbox, Field, Labeled, TextInputField } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { ISignUpFormValues } from './SignUpForm';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';
import { Link } from '~/components/atoms/Link';
import { AppRegionSelector } from '~/components/organisms/fields/AppRegionSelector';

const PASSWORD_MIN_LENGTH = 6;

export type ISignUpFormMainSectionProps = IContainerProps & {
  form: UseFormReturn<ISignUpFormValues>;
  defaultValues?: DefaultValues<ISignUpFormValues>;
};

export const SignUpFormMainSection: React.FC<ISignUpFormMainSectionProps> = (
  props,
) => {
  const { sx, form, defaultValues } = props;
  const { t } = useTranslation();

  return (
    <FormSection sx={sx}>
      <Labeled
        label={t('common.firstName')}
        required
        readOnly={defaultValues?.firstName !== undefined}
      >
        <FormField
          name="firstName"
          control={form.control}
          as={TextInputField}
          forwardError
          autoComplete="given-name"
          autoCapitalize="words"
        />
      </Labeled>
      <Labeled
        label={t('common.lastName')}
        required
        readOnly={defaultValues?.lastName !== undefined}
      >
        <FormField
          name="lastName"
          control={form.control}
          as={TextInputField}
          forwardError
          autoComplete="family-name"
          autoCapitalize="words"
        />
      </Labeled>
      <Labeled
        label={t('SignUpForm.phone-number')}
        required
        readOnly={defaultValues?.phoneNumber !== undefined}
      >
        <FormField
          name="phoneNumber"
          control={form.control}
          as={TextInputField}
          forwardError
          type="tel"
          autoComplete="tel"
          autoCapitalize="off"
        />
      </Labeled>
      <Labeled
        label={t('common.emailAddress')}
        required
        readOnly={defaultValues?.email !== undefined}
      >
        <FormField
          name="email"
          control={form.control}
          as={TextInputField}
          forwardError
          type="email"
          autoComplete="email"
          autoCapitalize="off"
        />
      </Labeled>
      <Labeled label={t('common.password')} required>
        <FormField
          name="password"
          control={form.control}
          as={TextInputField}
          forwardError
          rules={{
            minLength: {
              value: PASSWORD_MIN_LENGTH,
              message: t('ResetPasswordFormMainSection.password-too-short', {
                minLength: PASSWORD_MIN_LENGTH,
              }),
            },
          }}
          type="password"
          autoComplete="new-password"
        />
      </Labeled>
      <Labeled label={t('SignUpForm.confirm-password')} required>
        <FormField
          name="passwordConfirmation"
          control={form.control}
          as={TextInputField}
          forwardError
          rules={{
            validate: (value) => {
              if (form.watch('password') !== value) {
                return "Passwords don't match.";
              }
            },
          }}
          type="password"
          autoComplete="new-password"
        />
      </Labeled>
      <Labeled label={t('common.region')} required>
        {defaultValues?.appRegion ? (
          <Field readOnly>{defaultValues?.appRegion}</Field>
        ) : (
          <FormField
            name="appRegion"
            control={form.control}
            as={AppRegionSelector}
            forwardError
          />
        )}
      </Labeled>
      {/* TODO: use an accordion to show affiliate code input text */}
      <Labeled label={t('SignUpForm.affiliate-code-optional')}>
        {defaultValues?.affiliateCode ? (
          <Field readOnly>{defaultValues.affiliateCode}</Field>
        ) : (
          <FormField
            name="affiliateCode"
            control={form.control}
            as={TextInputField}
            forwardError
            autoComplete="off"
            autoCapitalize="characters"
          />
        )}
      </Labeled>
      <Labeled
        label={
          <>
            {t('SignUpForm.i-agree-to')}
            &nbsp;
            <Link to="https://crowdaa-tos.s3.amazonaws.com/crowdaa_conditions_generales_de_vente.pdf">
              {t('SignUpForm.terms-of-service')}
            </Link>
          </>
        }
        labelPosition="right"
        required
      >
        <FormField
          name="termsOfServiceAgreement"
          control={form.control}
          as={Checkbox}
        />
      </Labeled>
      <Labeled
        label={
          <>
            {t('SignUpForm.i-agree-to')}&nbsp;
            <Link to="https://crowdaa.com/wp-content/uploads/2022/07/licence_utilisation_20220705.pdf">
              {t('SignUpForm.user-license')}
            </Link>
          </>
        }
        labelPosition="right"
      >
        <FormField
          name="userLicenceAgreement"
          control={form.control}
          as={Checkbox}
          required
        />
      </Labeled>
    </FormSection>
  );
};
